import React from "react"
import { FormContainer, Worksheet, LabeledInput, formatCurrency } from "uikit"
import { ReactComponent as CautionIcon } from "uikit/Icons/caution.svg"
import { Dropdown, Grid } from "semantic-ui-react"
import { Footer } from "./Footer"
import { incomeTypes } from "components/incomes/incomeTypes"
import { useStore } from "store"

const RetirementForm = ({
  data: {
    fields: {
      is_client_age_modified,
      is_spouse_age_modified,
      isAmountChanged,
      client,
      spouse,
      amount
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  currentGeneralSpending,
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  clientAgeOptions,
  spouseAgeOptions,
  clientEarlyRetirementOptions,
  spouseEarlyRetirementOptions,
  spouseInfo,
  clientInfo,
  isNew,
  retirementAmountOptions,
  hasAgeWarning,
  clientIncomes,
  spouseIncomes
}) => {
  let isClientAgeInputVisible =
    !clientAgeOptions.find(
      option => option.value === client.fields.max_retirement_age.value
    ) &&
    (is_client_age_modified.value === "true" || !isNew)

  const isAmountInputVisible =
    !retirementAmountOptions.find(option => option.value === amount.value) &&
    (isAmountChanged.value === "true" || !isNew)

  let isSpouseAgeInputVisible =
    spouseAgeOptions &&
    !spouseAgeOptions.find(
      option => option.value === spouse.fields.max_retirement_age.value
    ) &&
    (is_spouse_age_modified.value === "true" || !isNew)

  clientEarlyRetirementOptions = getEarlyRetirementOptions(
    clientAgeOptions,
    client.fields.max_retirement_age.value
  )

  spouseEarlyRetirementOptions = spouseAgeOptions
    ? getEarlyRetirementOptions(
        spouseAgeOptions,
        spouse ? spouse.fields.max_retirement_age.value : 68
      )
    : []

  client.hasSpouse = spouseInfo ? true : false
  client.baseAge = getBaseAge(clientInfo.birth_date)
  if (client.hasSpouse) {
    spouse.baseAge = getBaseAge(spouseInfo.birth_date)
  }
  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        {clientIncomes.length > 0 ? (
          <Worksheet.Row
            label="What age would you like to retire by?"
            description="Your latest retirement year will be the year you'll be this age on January 1st."
            paddedField
          >
            <Worksheet.Field
              error={
                isClientAgeInputVisible
                  ? null
                  : client.fields.max_retirement_age.error
              }
            >
              <div>
                <Dropdown
                  {...client.fields.max_retirement_age}
                  onChange={(ev, data) => {
                    client.fields.max_retirement_age.onChange(ev, data)
                    if (is_client_age_modified.value === "false") {
                      is_client_age_modified.onChange(ev, { value: "true" })
                    }
                  }}
                  fluid
                  selection
                  placeholder="Age"
                  options={clientAgeOptions.concat([
                    {
                      value: isClientAgeInputVisible
                        ? client.fields.max_retirement_age.value
                        : "58",
                      text: "Other"
                    }
                  ])}
                />
              </div>
            </Worksheet.Field>
            {isClientAgeInputVisible ? (
              <Worksheet.Row label="Please enter age" paddedField>
                <Worksheet.Field error={client.fields.max_retirement_age.error}>
                  <input
                    {...client.fields.max_retirement_age}
                    placeholder="Years"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
            ) : null}
          </Worksheet.Row>
        ) : null}
        {clientIncomes.length > 0 ? (
          <Worksheet.Row
            label="How many years earlier would you consider retiring?"
            paddedField
          >
            <Worksheet.Field error={client.fields.early_retirement_years.error}>
              <Dropdown
                fluid
                {...client.fields.early_retirement_years}
                selection
                placeholder="Years"
                options={clientEarlyRetirementOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}

        {spouseInfo && spouseIncomes.length > 0 ? (
          <Worksheet.Row
            label={`What age would ${spouseInfo.first_name} like to retire by?`}
            description={`${spouseInfo.first_name}'s latest retirement year will be the year ${spouseInfo.first_name} will be this age on January 1st.`}
            paddedField
          >
            <Worksheet.Field
              error={
                isSpouseAgeInputVisible
                  ? null
                  : spouse.fields.max_retirement_age.error
              }
            >
              <Dropdown
                {...spouse.fields.max_retirement_age}
                onChange={(ev, data) => {
                  spouse.fields.max_retirement_age.onChange(ev, data)
                  if (is_spouse_age_modified.value === "false") {
                    is_spouse_age_modified.onChange(ev, { value: "true" })
                  }
                }}
                fluid
                selection
                placeholder="Age"
                options={spouseAgeOptions.concat([
                  {
                    value: isSpouseAgeInputVisible
                      ? spouse.fields.max_retirement_age.value
                      : "55",
                    text: "Other"
                  }
                ])}
              />
            </Worksheet.Field>
            {isSpouseAgeInputVisible ? (
              <Worksheet.Row label="Please enter age" paddedField>
                <Worksheet.Field error={spouse.fields.max_retirement_age.error}>
                  <input
                    {...spouse.fields.max_retirement_age}
                    placeholder="Years"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
            ) : null}
          </Worksheet.Row>
        ) : null}
        {spouseInfo && spouseIncomes.length > 0 ? (
          <Worksheet.Row
            label={`How many years earlier would ${spouseInfo.first_name} consider retiring?`}
            paddedField
          >
            <Worksheet.Field error={spouse.fields.early_retirement_years.error}>
              <Dropdown
                fluid
                {...spouse.fields.early_retirement_years}
                placeholder="Years"
                selection
                options={spouseEarlyRetirementOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        <Worksheet.Row
          label="How much of your current general spending do you want in retirement?"
          description={
            <>
              General spending covers daily spending beyond debt payments,
              taxes, and other specific goals and expenses you’ve told us about.
              Your current general spending is{" "}
              {formatCurrency(currentGeneralSpending)}.
            </>
          }
          paddedField
        >
          <Worksheet.Field error={isAmountInputVisible ? null : amount.error}>
            <Dropdown
              {...amount}
              fluid
              selection
              placeholder="Amount"
              options={retirementAmountOptions.concat([
                {
                  value: isAmountInputVisible ? amount.value : "20000",
                  text: "Other"
                }
              ])}
              onChange={(ev, data) => {
                amount.onChange(ev, data)
                if (isAmountChanged.value === "false") {
                  isAmountChanged.onChange(ev, { value: "true" })
                }
              }}
            />
          </Worksheet.Field>
          {isAmountInputVisible ? (
            <Worksheet.Row label="Please enter amount" paddedField>
              <Worksheet.Field error={amount.error}>
                <LabeledInput
                  {...amount}
                  label=""
                  iconText="$"
                  iconPosition="left"
                  placeholder="0"
                />
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>
      </Worksheet.Body>
      {hasAgeWarning(
        client.fields.max_retirement_age,
        client.fields.early_retirement_years
      ) ? (
        <div style={{ width: "100%", borderTop: "1px solid #ddd" }}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={1} style={{ paddingLeft: "1em" }}>
                <CautionIcon transform="scale(0.5)" />
              </Grid.Column>
              <Grid.Column
                width={15}
                textAlign="left"
                style={{ paddingTop: "0.5em" }}
              >
                <p>
                  If retiring before Medicare eligibility age, you will need to
                  take into consideration the cost of health insurance during
                  the first years of your retirement.
                </p>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      ) : null}
      <Footer
        baseUrl={baseUrl}
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={values => {
          if (!hasError) onSubmit(values)
        }}
        isPending={isPending}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Goal"
        error={error}
      />
    </Worksheet>
  )
}

const RetirementFormContainer = FormContainer(
  {
    is_client_age_modified: {
      type: "boolean",
      defaultValue: "false",
      submit: false
    },
    isAmountChanged: {
      type: "boolean",
      defaultValue: "false",
      submit: false
    },
    is_spouse_age_modified: {
      type: "boolean",
      defaultValue: "false",
      submit: false
    },
    client: {
      type: "object",
      schema: {
        max_retirement_age: {
          type: "unit",
          useGrouping: false,
          defaultValue: 68
        },
        early_retirement_years: {
          type: "unit",
          useGrouping: false,
          defaultValue: 0
        }
      }
    },
    spouse: {
      type: "object",
      schema: {
        max_retirement_age: {
          type: "unit",
          useGrouping: false,
          defaultValue: 68
        },
        early_retirement_years: {
          type: "unit",
          useGrouping: false,
          defaultValue: 0
        }
      }
    },
    amount: {
      type: "unit"
    },
    type: {
      type: "string",
      defaultValue: "retirement"
    }
  },
  ({ fields }) => {
    const { client, spouse, amount } = fields
    const clientMaxAge = +client.fields.max_retirement_age.value
    if (client.hasSpouse === false) {
      if (parseInt(amount.value.split(",").join("")) < 5000) {
        return {
          fields: {
            amount: "Too small! Must be at least 5,000."
          }
        }
      }
    } else {
      if (parseInt(amount.value.split(",").join("")) < 10000) {
        return {
          fields: {
            amount: "Too small! Must be at least 10,000."
          }
        }
      }

      const spouseMaxAge = +spouse.fields.max_retirement_age.value
      if (spouseMaxAge < spouse.baseAge) {
        return {
          fields: {
            "spouse/max_retirement_age": `Too small! Must be at least ${spouse.baseAge}.`
          }
        }
      }
      if (spouseMaxAge > Math.max(100, spouse.baseAge + 10)) {
        return {
          fields: {
            "spouse/max_retirement_age": `Too large! Must not be greater than ${Math.max(
              100,
              spouse.baseAge + 10
            )}.`
          }
        }
      }
    }
    if (clientMaxAge < client.baseAge) {
      return {
        fields: {
          "client/max_retirement_age": `Too small! Must be at least ${client.baseAge}.`
        }
      }
    }
    if (clientMaxAge > Math.max(100, client.baseAge + 10)) {
      return {
        fields: {
          "client/max_retirement_age": `Too large! Must not be greater than ${Math.max(
            100,
            client.baseAge + 10
          )}.`
        }
      }
    }
  }
)(RetirementForm)

const getBaseAge = bday => {
  const currentYear = new Date().getFullYear()
  const birthDate = new Date(bday)
  const baseAge = currentYear - birthDate.getFullYear() - 1
  return baseAge
}

const getAgeOptions = birthDate => {
  birthDate = new Date(birthDate)
  const currentYear = new Date().getFullYear()
  // baseAge is the age on January 1st as the optimizer
  // interprets retirement year as the year your that
  // age on Jan 1st.
  let baseAge = currentYear - birthDate.getFullYear() - 1
  if (birthDate.getMonth() === 1 && birthDate.getDay() === 1) {
    baseAge += 1
  }
  let optionCount = Math.max(72, baseAge + 7) - Math.max(65, baseAge)
  const delta = baseAge < 65 ? 65 - baseAge : 0
  return Array(optionCount)
    .fill()
    .map((_, index) => {
      const age = baseAge + index + delta
      const when = currentYear + index + delta
      return { value: age + "", text: `${age} (${when})` }
    })
}

const getYearsOptions = (options, maxAge) => {
  const option = options.filter(el => el.value === maxAge.toString())
  const year = option.length ? option[0].text : options[options.length - 1].text
  const closestYear = year
    .split("(")[1]
    .split(")")
    .join("")
  const diffYears = parseInt(closestYear) - new Date().getFullYear() + 1
  return parseInt(diffYears)
}

const getEarlyRetirementOptions = (options, age) => {
  const diff = getYearsOptions(options, age)
  return Array(diff < 6 ? diff : 6)
    .fill()
    .map((_, i) => ({ value: i + "", text: i }))
}

const Retirement = ({ household, goal, scenario, ...formProps }) => {
  const [people] = useStore("householdPeople", household.id)
  const [incomesObject] = useStore("householdIncomes", household.id)
  const [incomeBreakdownObject, { addGoal, updateGoal, deleteGoal }] = useStore(
    "householdIncomeBreakdowns",
    household.id
  )

  const currentGeneralSpending = incomeBreakdownObject.general_spending
  let values = {}
  const isNew = goal === null
  const incomes = Object.values(incomesObject)
  const spouseInfo = Object.values(people).find(
    person => person.role === "spouse"
  )
  const clientInfo = Object.values(people).find(
    person => person.role === "client"
  )

  if (!isNew) {
    values = goal
  } else {
    const currentYear = new Date().getFullYear()
    let birthDate = new Date(clientInfo.birth_date)
    let baseAge = currentYear - birthDate.getFullYear() - 1
    if (birthDate.getMonth() === 1 && birthDate.getDay() === 1) {
      baseAge += 1
    }
    values = {
      client: { max_retirement_age: Math.max(65, baseAge) }
    }
  }
  var clientIncomes = incomes.filter(
    income =>
      income.person_id === clientInfo.id &&
      (incomeTypes[income.type].cardType === "Employment" ||
        incomeTypes[income.type].cardType === "SelfEmployment")
  )
  var spouseIncomes = incomes.filter(
    income =>
      spouseInfo &&
      income.person_id === spouseInfo.id &&
      (incomeTypes[income.type].cardType === "Employment" ||
        incomeTypes[income.type].cardType === "SelfEmployment")
  )
  const clientAgeOptions = getAgeOptions(clientInfo.birth_date)
  const spouseAgeOptions = spouseInfo
    ? getAgeOptions(spouseInfo.birth_date)
    : null

  const clientEarlyRetirementOptions = getEarlyRetirementOptions(
    clientAgeOptions,
    values.client ? values.client.max_retirement_age : 68
  )

  const retirementAmountOptions = Array(3)
    .fill(20)
    .map((v, i) => {
      const value = Math.round(
        v * (i + 4) * (Math.round(currentGeneralSpending / 1000) * 1000) * 0.01
      )
      return {
        value: value.toLocaleString(),
        text: `${v * (i + 4)}% (${formatCurrency(value)})`,
        key: `${"amountOption_" + i}`
      }
    })

  const hasAgeWarning = (retirementAge, earlyYears) => {
    return retirementAge.value - (earlyYears.value || 0) < 65
  }
  let hasDataChange = false
  if (
    (clientIncomes.length === 0 && values.client) ||
    (values.spouse && spouseIncomes.length === 0)
  ) {
    hasDataChange = true
  }
  const submit = isNew
    ? async values => {
        const payload = Object.assign(
          {},
          {
            ...values,
            client: clientIncomes.length > 0 ? values.client : null,
            spouse:
              spouseInfo && spouseIncomes.length > 0 ? values.spouse : null
          }
        )
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...payload
        })
      }
    : async values => {
        Object.assign(values, {
          client: clientIncomes.length > 0 ? values.client : null,
          spouse: spouseInfo && spouseIncomes.length > 0 ? values.spouse : null
        })
        await updateGoal({ id: goal.id, ...values })
      }

  const spouseEarlyRetirementOptions = spouseAgeOptions
    ? getEarlyRetirementOptions(
        spouseAgeOptions,
        values.spouse ? values.spouse.max_retirement_age : 68
      )
    : []

  const onDelete = async () => {
    await deleteGoal({ id: goal.id, scenario_id: scenario.id })
    return true
  }

  return (
    <RetirementFormContainer
      {...{
        submit,
        onDelete,
        values,
        spouseInfo,
        clientInfo,
        currentGeneralSpending,
        clientAgeOptions,
        spouseAgeOptions,
        isNew,
        retirementAmountOptions,
        hasAgeWarning,
        clientIncomes,
        spouseIncomes,
        clientEarlyRetirementOptions,
        spouseEarlyRetirementOptions,
        forceModified: hasDataChange,
        ...formProps
      }}
    />
  )
}

export default Retirement
